import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { API, useAuth } from "../App";
import axios from "axios";
import { toast } from "sonner";
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Download,
  ExternalLink,
  MapPin,
  FileText,
  Clock,
  CheckCircle2,
  X,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  subMonths,
  isSameMonth,
  isSameDay,
  isToday,
  parseISO,
} from "date-fns";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const EVENT_COLORS = {
  "Closing Date": { bg: "var(--t-primary)", text: "#fff" },
  "Inspection Deadline": { bg: "#D97706", text: "#fff" },
  "Financing Deadline": { bg: "#2563EB", text: "#fff" },
  "Earnest Money Due": { bg: "#059669", text: "#fff" },
  "Contract Date": { bg: "var(--t-text-muted)", text: "#fff" },
};

const getEventStyle = (event) => {
  if (event.type === "key_date") {
    return EVENT_COLORS[event.title] || { bg: "var(--t-text-muted)", text: "#fff" };
  }
  if (event.task_status === "completed") {
    return { bg: "var(--t-border)", text: "var(--t-text-muted)" };
  }
  return { bg: "var(--t-primary)", text: "#fff", opacity: 0.75 };
};

const makeGoogleCalUrl = (event) => {
  const d = event.date.replace(/-/g, "");
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: `${event.title} - ${event.address}`,
    dates: `${d}/${d}`,
    details: `Transaction: ${event.address}`,
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

const CalendarView = () => {
  const { user } = useAuth();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);

  const monthStr = format(currentMonth, "yyyy-MM");

  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/calendar/events?month=${monthStr}`, { withCredentials: true });
        setEvents(res.data);
      } catch (err) {
        toast.error("Failed to load calendar events");
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, [monthStr]);

  // Build calendar grid
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const gridStart = startOfWeek(monthStart);
    const gridEnd = endOfWeek(monthEnd);

    const days = [];
    let day = gridStart;
    while (day <= gridEnd) {
      days.push(day);
      day = addDays(day, 1);
    }
    return days;
  }, [currentMonth]);

  // Group events by date
  const eventsByDate = useMemo(() => {
    const map = {};
    events.forEach((e) => {
      if (!map[e.date]) map[e.date] = [];
      map[e.date].push(e);
    });
    return map;
  }, [events]);

  const selectedDayEvents = selectedDay
    ? eventsByDate[format(selectedDay, "yyyy-MM-dd")] || []
    : [];

  const handleExportICS = async () => {
    try {
      const res = await axios.get(`${API}/calendar/ics?month=${monthStr}`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `mattc-deadlines-${monthStr}.ics`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Calendar exported");
    } catch {
      toast.error("Failed to export calendar");
    }
  };

  const totalEvents = events.length;
  const keyDates = events.filter((e) => e.type === "key_date").length;
  const tasks = events.filter((e) => e.type === "task" && e.task_status !== "completed").length;

  return (
    <div className="p-6 md:p-8 animate-fade-in">
      {/* Header */}
      <div className="page-header -mx-6 md:-mx-8 -mt-6 md:-mt-8 mb-6 px-6 md:px-8 py-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ color: "var(--t-text)", fontFamily: "var(--t-heading-font)" }}>
              Calendar
            </h1>
            <p className="mt-1" style={{ color: "var(--t-text-muted)" }}>
              {user?.role === "super_admin" ? "All transaction deadlines" : "Your assigned deadlines"}
            </p>
          </div>
          <Button onClick={handleExportICS} className="btn-primary" data-testid="export-ics-btn">
            <Download className="w-4 h-4 mr-2" />
            Export to Google Calendar
          </Button>
        </div>
      </div>

      <div className="flex gap-6">
        {/* Calendar Grid */}
        <div className="flex-1">
          <Card className="card-surface" data-testid="calendar-card">
            {/* Month Navigation */}
            <CardHeader className="py-4 px-5 flex flex-row items-center justify-between" style={{ borderBottom: "1px solid var(--t-border)" }}>
              <div className="flex items-center gap-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                  data-testid="prev-month-btn"
                  className="h-8 w-8 p-0"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <h2 className="text-lg font-semibold min-w-[180px] text-center" style={{ fontFamily: "var(--t-heading-font)", color: "var(--t-text)" }} data-testid="current-month-label">
                  {format(currentMonth, "MMMM yyyy")}
                </h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                  data-testid="next-month-btn"
                  className="h-8 w-8 p-0"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex items-center gap-3 text-xs" style={{ color: "var(--t-text-muted)" }}>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setCurrentMonth(new Date()); setSelectedDay(new Date()); }}
                  data-testid="today-btn"
                  className="h-7 px-3 text-xs font-semibold"
                  style={{ borderColor: "var(--t-border)", color: "var(--t-text)" }}
                >
                  Today
                </Button>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: "var(--t-primary)" }}></span> {keyDates} key dates</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: "var(--t-primary)", opacity: 0.6 }}></span> {tasks} tasks</span>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {/* Weekday headers */}
              <div className="grid grid-cols-7" style={{ borderBottom: "1px solid var(--t-border)" }}>
                {WEEKDAYS.map((d) => (
                  <div key={d} className="py-2 text-center text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--t-text-muted)" }}>
                    {d}
                  </div>
                ))}
              </div>

              {/* Days grid */}
              <div className="grid grid-cols-7">
                {calendarDays.map((day, i) => {
                  const dateStr = format(day, "yyyy-MM-dd");
                  const dayEvents = eventsByDate[dateStr] || [];
                  const inMonth = isSameMonth(day, currentMonth);
                  const today = isToday(day);
                  const selected = selectedDay && isSameDay(day, selectedDay);

                  return (
                    <div
                      key={dateStr}
                      onClick={() => setSelectedDay(day)}
                      data-testid={`cal-day-${dateStr}`}
                      className="min-h-[90px] p-1.5 cursor-pointer transition-all border-b border-r"
                      style={{
                        borderColor: "var(--t-border-light)",
                        background: selected ? "var(--t-primary)" + "0C" : today ? "var(--t-bg)" : "transparent",
                        opacity: inMonth ? 1 : 0.35,
                      }}
                    >
                      <div className={`text-xs font-medium mb-1 flex items-center justify-center w-6 h-6 rounded-full ${today ? "text-white" : ""}`}
                        style={{
                          background: today ? "var(--t-primary)" : "transparent",
                          color: today ? "#fff" : "var(--t-text)",
                        }}
                      >
                        {format(day, "d")}
                      </div>
                      <div className="space-y-0.5">
                        {dayEvents.slice(0, 3).map((ev) => {
                          const style = getEventStyle(ev);
                          return (
                            <div
                              key={ev.id}
                              className="text-[10px] leading-tight px-1.5 py-0.5 rounded truncate font-medium"
                              style={{ background: style.bg, color: style.text, opacity: style.opacity || 1 }}
                              title={`${ev.title} — ${ev.address}`}
                            >
                              {ev.title}
                            </div>
                          );
                        })}
                        {dayEvents.length > 3 && (
                          <div className="text-[10px] px-1.5 font-medium" style={{ color: "var(--t-text-muted)" }}>
                            +{dayEvents.length - 3} more
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Day Detail Panel */}
        <div className="w-80 flex-shrink-0">
          <Card className="card-surface sticky top-6" data-testid="day-detail-panel">
            <CardHeader className="py-3 px-5" style={{ borderBottom: "1px solid var(--t-border)" }}>
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold uppercase tracking-wider" style={{ color: "var(--t-text)", fontFamily: "var(--t-heading-font)" }}>
                  {selectedDay ? format(selectedDay, "EEEE, MMM d") : "Select a Day"}
                </CardTitle>
                {selectedDay && (
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setSelectedDay(null)}>
                    <X className="w-3.5 h-3.5" style={{ color: "var(--t-text-subtle)" }} />
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {selectedDayEvents.length > 0 ? (
                <div className="divide-y" style={{ borderColor: "var(--t-border-light)" }}>
                  {selectedDayEvents.map((ev) => {
                    const style = getEventStyle(ev);
                    return (
                      <div key={ev.id} className="px-4 py-3" data-testid={`day-event-${ev.id}`}>
                        <div className="flex items-start gap-3">
                          <div className="mt-0.5 w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: style.bg }} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate" style={{ color: "var(--t-text)" }}>
                              {ev.title}
                            </p>
                            <p className="text-xs flex items-center gap-1 mt-0.5" style={{ color: "var(--t-text-muted)" }}>
                              <MapPin className="w-3 h-3" /> {ev.address}
                            </p>
                            <div className="flex items-center gap-2 mt-2">
                              <Link to={`/transactions/${ev.transaction_id}`}>
                                <Badge variant="outline" className="text-[10px] cursor-pointer hover:opacity-80" style={{ borderColor: "var(--t-border)", color: "var(--t-text-muted)" }}>
                                  <FileText className="w-3 h-3 mr-1" /> View
                                </Badge>
                              </Link>
                              <a href={makeGoogleCalUrl(ev)} target="_blank" rel="noopener noreferrer">
                                <Badge variant="outline" className="text-[10px] cursor-pointer hover:opacity-80" style={{ borderColor: "var(--t-border)", color: "var(--t-text-muted)" }}>
                                  <ExternalLink className="w-3 h-3 mr-1" /> Add to Google Cal
                                </Badge>
                              </a>
                            </div>
                          </div>
                          {ev.type === "task" && (
                            <div className="flex-shrink-0">
                              {ev.task_status === "completed" ? (
                                <CheckCircle2 className="w-4 h-4 text-green-600" />
                              ) : (
                                <Clock className="w-4 h-4" style={{ color: "var(--t-text-subtle)" }} />
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="px-4 py-10 text-center">
                  <CalendarIcon className="w-10 h-10 mx-auto mb-3" style={{ color: "var(--t-border)" }} />
                  <p className="text-sm" style={{ color: "var(--t-text-muted)" }}>
                    {selectedDay ? "No events on this day" : "Click a day to view details"}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CalendarView;
